import styled from "styled-components";
import { Link } from "react-router-dom";

export const DetailContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    overflow: visible;
  }
`;

export const InlineActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 0.8rem;
  line-height: 1;
  border-radius: 999px;
  color: var(--color-text-secondary);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const BackdropImage = styled.div<{ backdrop: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80vh;
  background: ${(props) => `
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.4) 50%,
      rgba(0, 0, 0, 0.9) 100%
    ),
    linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.9) 0%,
      rgba(0, 0, 0, 0.1) 20%,
      rgba(0, 0, 0, 0.1) 80%,
      rgba(0, 0, 0, 0.9) 100%
    ),
    url(https://image.tmdb.org/t/p/original${props.backdrop})`};
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  z-index: 0;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  
  @media (max-width: 768px) {
    height: 60vh;
    background: ${(props) => `
      linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.4) 0%,
        rgba(0, 0, 0, 0.6) 50%,
        rgba(0, 0, 0, 0.95) 100%
      ),
      url(https://image.tmdb.org/t/p/w780${props.backdrop})`};
    background-size: cover;
    background-position: top center;
    background-attachment: scroll;
    will-change: auto;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      transparent 0%,
      rgba(0, 0, 0, 0.1) 70%,
      rgba(0, 0, 0, 0.3) 100%
    );
  }
`;

export const Content = styled.div`
  padding: var(--spacing-4);
  padding-top: 40vh;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  z-index: 1;

  @media (max-width: 768px) {
    padding: var(--spacing-3);
    padding-top: 30vh;
    position: relative;
    will-change: transform;
  }
`;

export const InfoContainer = styled.div`
  flex: 1;
  margin-left: 240px;
  color: var(--color-text-primary);

  @media (max-width: 768px) {
    margin-left: 0;
    padding: var(--spacing-3);
    background: rgba(0, 0, 0, 0.7);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    position: relative;
    z-index: 3;
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
`;

export const PosterContainer = styled.div`
  overflow: hidden;
  position: absolute;
  top: 30vh;
  left: var(--spacing-4);
  width: 220px;
  height: 330px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 2;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 160px;
    height: 240px;
    margin-bottom: var(--spacing-3);
  }
`;

export const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-3);
  position: relative;
  z-index: 3;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
`;

export const MetaInfo = styled.div`
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-4);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    gap: var(--spacing-3);
  }
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: var(--color-text-secondary);

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const Overview = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-secondary);

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: justify;
    padding: var(--spacing-3);
    background: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: var(--spacing-3);
  margin: var(--spacing-4) 0;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-2);
    margin: var(--spacing-3) 0;

    button {
      width: 100%;
      max-width: none;
    }
  }

  button {
    max-width: 200px;
    border-radius: 28px;
    padding: var(--spacing-1) var(--spacing-3);
    font-weight: 500;
    letter-spacing: 0.2px;
    text-transform: none !important;
    font-size: 0.85rem;
    color: var(--color-text-secondary);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    background: rgba(255, 255, 255, 0.04) !important;
    box-shadow: none !important;
    backdrop-filter: blur(6px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      border-color: rgba(255, 255, 255, 0.28) !important;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    /* Subtle differentiation when primary is active, staying discreet */
    &[data-variant="primary"] {
      background: rgba(255, 215, 0, 0.12) !important;
      border-color: rgba(255, 215, 0, 0.35) !important;
      color: #fff !important;
    }

    svg {
      width: 16px;
      height: 16px;
    }

    @media (max-width: 768px) {
      font-size: 0.8rem;
      padding: var(--spacing-1) var(--spacing-2);
    }
  }
`;

export const GenresContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-4);

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

export const GenreTag = styled(Link)`
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);
  padding: var(--spacing-2) var(--spacing-3);
  background: rgba(226, 182, 22, 0.1);
  color: var(--color-primary);
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid rgba(226, 182, 22, 0.3);

  &:hover {
    background: rgba(226, 182, 22, 0.2);
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: var(--spacing-1) var(--spacing-2);
  }
`;
